import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import { FullPageLoader } from '../ui/full-page-loader';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useAnalytics } from '../../hooks/useAnalytics';
import { 
  Users, 
  Building2, 
  Activity, 
  Shield, 
  Plus,
  Settings,
  BarChart3,
  LogOut,
  Search,
  TrendingUp,
  Globe,
  CheckCircle,
  Palette,
  Layers,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import OrganizationConnections from '../organization/OrganizationConnections';
import BaseTemplateManagement from './BaseTemplateManagement';
import { ComingSoonPill } from '../ui/coming-soon-pill';

// Organization creation interface
interface OrganizationCreateRequest {
  name: string;
  registration_number?: string;
  subdomain: string;
  organisation_email: string;
}

// Organization data interface from API
interface Organization {
  org_id: string;
  org_name: string;
  status?: string;
  user_count?: number;
  credential_count?: number;
  created_at?: string;
}





export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const { platformData, isLoading: analyticsLoading, error: analyticsError, refreshData } = useAnalytics();
  const { notifyApiSuccess, notifyApiError } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [activeTab, setActiveTab] = useState('tenants');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<OrganizationCreateRequest>({
    name: '',
    registration_number: '',
    subdomain: '',
    organisation_email: ''
  });

  // Reset state when user changes (fresh login)
  useEffect(() => {
    setSearchTerm('');
    setShowCreateTenant(false);
    setFormData({ name: '', registration_number: '', subdomain: '', organisation_email: '' });
    setCreateError(null);
    setCreateSuccess(false);
    setActiveTab('tenants');
  }, [user?.id]);

  const handleInputChange = (field: keyof OrganizationCreateRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setCreateError(null); // Clear error when user starts typing
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Organization name is required';
    if (formData.name.length < 3 || formData.name.length > 100) {
      return 'Organization name must be between 3-100 characters';
    }
    if (!formData.subdomain.trim()) return 'Subdomain is required';
    if (formData.subdomain.length < 1 || formData.subdomain.length > 100) {
      return 'Subdomain must be between 1-100 characters';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.organisation_email)) {
      return 'Please enter a valid email address';
    }
    if (formData.registration_number && formData.registration_number.length > 50) {
      return 'Registration number must be max 50 characters';
    }
    return null;
  };

  const handleCreateTenant = async () => {
    const validationError = validateForm();
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      // Prepare request data, excluding empty optional fields
      const requestData: OrganizationCreateRequest = {
        name: formData.name.trim(),
        subdomain: formData.subdomain.trim(),
        organisation_email: formData.organisation_email.trim(),
        ...(formData.registration_number?.trim() && { registration_number: formData.registration_number.trim() })
      };

      await apiRequest('/api/organizations/create', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      setCreateSuccess(true);
      setFormData({ name: '', registration_number: '', subdomain: '', organisation_email: '' });
      notifyApiSuccess('Organization', 'created', requestData.name);
      // Refresh analytics data to get updated organizations list (non-blocking)
      void refreshData().catch((err) => {
        console.error('Failed to refresh analytics after org creation:', err);
      });
      setTimeout(() => {
        setShowCreateTenant(false);
        setCreateSuccess(false);
      }, 2000);

    } catch (error: any) {
      console.error('Error creating organization:', error);
      const message = error.message || 'Failed to create organization';
      setCreateError(message);
      notifyApiError('Organization', 'create', message);
    } finally {
      setIsCreating(false);
    }
  };

  // Use existing analytics data for organizations
  const organizations = platformData?.organizations || [];
  const organizationsLoading = analyticsLoading;
  const organizationsError = analyticsError;

  // Transform organizations data to match the expected tenant structure
  const transformedTenants = organizations.map(org => ({
    id: org.org_id,
    name: org.org_name,
    subdomain: org.org_name.toLowerCase().replace(/\s+/g, '').substring(0, 10), // Generate subdomain from name
    status: org.status || 'active', // Use actual status if available
    users: org.user_count || 0,
    credentials: org.credential_count || 0,
    verifications: Math.floor((org.credential_count || 0) * 2.5), // Estimate verifications based on credentials
    createdAt: org.created_at || '2024-01-01', // Use actual creation date if available
    growth: org.user_count > 0 ? '+active' : 'new'
  }));

  const filteredTenants = transformedTenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-cool-gray-100">
      {isCreating && <FullPageLoader message="Creating organization..." />}
      {/* Header */}
      <header className="credential-capital-header shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 p-2 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">
                  Credential Capital Admin
                </h1>
                <p className="text-blue-100">
                  Platform Management Console
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 text-blue-100">
                <Globe className="h-4 w-4" />
                <span className="text-sm">Platform Status: Active</span>
              </div>
              <span className="text-blue-100">Welcome, {user?.name}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                className="credential-capital-button-header"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6 bg-[rgba(251,24,24,0)]">
        {/* Enhanced Metrics Cards - Real Data */}
        {platformData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Total Organizations</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{platformData.total_organizations}</p>
                    <p className="text-sm text-verified-green flex items-center mt-1">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {platformData.organizations.filter(org => org.user_count > 0).length} active
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl">
                    <Building2 className="h-8 w-8 text-electric-steel" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Total Users</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{platformData.total_users.toLocaleString()}</p>
                    <p className="text-sm text-verified-green flex items-center mt-1">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {platformData.organizations.length > 0 ? 'Active platform' : 'Getting started'}
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-xl">
                    <Users className="h-8 w-8 text-verified-green" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Credentials Issued</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{platformData.total_credentials.toLocaleString()}</p>
                    <p className="text-sm text-electric-steel flex items-center mt-1">
                      <Shield className="h-3 w-3 mr-1" />
                      All verified
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl">
                    <Shield className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Templates</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{platformData.total_templates.toLocaleString()}</p>
                    <p className="text-sm text-orange-600 flex items-center mt-1">
                      <Activity className="h-3 w-3 mr-1" />
                      Available
                    </p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-xl">
                    <Layers className="h-8 w-8 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Avg Credentials/Org</p>
                    <p className="text-3xl font-bold text-charcoal-ink">
                      {platformData.total_organizations > 0 ? Math.round(platformData.total_credentials / platformData.total_organizations) : 0}
                    </p>
                    <p className="text-sm text-electric-steel flex items-center mt-1">
                      <Layers className="h-3 w-3 mr-1" />
                      Per organization
                    </p>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-xl">
                    <Palette className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Platform Health</p>
                    <p className="text-3xl font-bold text-charcoal-ink">
                      {platformData.total_organizations > 0 && platformData.total_users > 0 ? 'Good' : 'Setup'}
                    </p>
                    <p className="text-sm text-verified-green flex items-center mt-1">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {platformData.total_organizations > 0 ? 'Active' : 'Initializing'}
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-xl">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Loading State for Metrics */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="credential-capital-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                      <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
                    </div>
                    <div className="bg-gray-100 p-3 rounded-xl w-14 h-14 animate-pulse"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide">
            <TabsList className="bg-white credential-capital-shadow min-w-max">
              <TabsTrigger value="tenants" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white whitespace-nowrap text-xs md:text-sm px-2 md:px-4">
                Tenant Management
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white whitespace-nowrap text-xs md:text-sm px-2 md:px-4">
                <Palette className="h-4 w-4 mr-2" />
                Base Templates
              </TabsTrigger>
              <TabsTrigger value="connections" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white whitespace-nowrap text-xs md:text-sm px-2 md:px-4">
                Network Connections
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white whitespace-nowrap text-xs md:text-sm px-2 md:px-4">
                Platform Analytics
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white whitespace-nowrap text-xs md:text-sm px-2 md:px-4">
                System Settings
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="tenants" className="space-y-6">
            <Card className="credential-capital-card">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <div>
                    <CardTitle className="text-xl font-display text-charcoal-ink">
                      Tenant Organizations
                    </CardTitle>
                    <CardDescription className="text-warm-slate">
                      Manage tenant organizations and their access to the platform
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={refreshData}
                      disabled={organizationsLoading}
                      className="credential-capital-button-secondary"
                    >
                      <RefreshCw className={`h-4 w-4 ${organizationsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button 
                      onClick={() => setShowCreateTenant(!showCreateTenant)}
                      className="credential-capital-button-primary mobile:mobile-button-text mobile:mobile-button-stack"
                    >
                      <Plus className="h-4 w-4 mobile:mr-0" />
                      New Tenant
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-warm-slate" />
                      <Input
                        placeholder="Search tenants..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="credential-capital-input pl-10"
                      />
                    </div>
                  </div>

                  {showCreateTenant && (
                    <Card className="border-2 border-electric-steel/20 bg-blue-50/50">
                      <CardHeader>
                        <CardTitle className="text-lg font-display text-charcoal-ink">
                          Create New Tenant
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {createError && (
                          <Alert className="mb-4 border-red-200 bg-red-50">
                            <AlertDescription className="text-red-700">
                              {createError}
                            </AlertDescription>
                          </Alert>
                        )}
                        
                        {createSuccess && (
                          <Alert className="mb-4 border-green-200 bg-green-50">
                            <AlertDescription className="text-green-700">
                              Organization created successfully!
                            </AlertDescription>
                          </Alert>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="tenant-name" className="text-charcoal-ink">
                              Organization Name *
                            </Label>
                            <Input 
                              id="tenant-name" 
                              placeholder="e.g., State University"
                              value={formData.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              className="credential-capital-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subdomain" className="text-charcoal-ink">
                              Subdomain *
                            </Label>
                            <Input 
                              id="subdomain" 
                              placeholder="e.g., stateuniv"
                              value={formData.subdomain}
                              onChange={(e) => handleInputChange('subdomain', e.target.value)}
                              className="credential-capital-input"
                            />
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="admin-email" className="text-charcoal-ink">
                              Admin Email *
                            </Label>
                            <Input 
                              id="admin-email" 
                              placeholder="e.g., admin@stateuniv.edu"
                              value={formData.organisation_email}
                              onChange={(e) => handleInputChange('organisation_email', e.target.value)}
                              className="credential-capital-input"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="registration-number" className="text-charcoal-ink">
                              Registration Number (Optional)
                            </Label>
                            <Input 
                              id="registration-number" 
                              placeholder="e.g., REG123456"
                              value={formData.registration_number}
                              onChange={(e) => handleInputChange('registration_number', e.target.value)}
                              className="credential-capital-input"
                            />
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-6">
                          <Button 
                            onClick={handleCreateTenant}
                            disabled={isCreating}
                            className="credential-capital-button-primary mobile:mobile-button-text"
                          >
                            {isCreating ? 'Creating...' : 'Create Tenant'}
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => setShowCreateTenant(false)}
                            disabled={isCreating}
                            className="credential-capital-button-secondary mobile:mobile-button-text"
                          >
                            Cancel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="space-y-4">
                    {/* Loading State */}
                    {organizationsLoading ? (
                      <div className="space-y-4">
                        {[...Array(3)].map((_, index) => (
                          <Card key={index} className="credential-capital-card">
                            <CardContent className="pt-6">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="bg-gray-100 p-3 rounded-xl w-14 h-14 animate-pulse"></div>
                                  <div className="space-y-2">
                                    <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
                                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                                    <div className="h-6 bg-gray-200 rounded w-12 animate-pulse"></div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                  <div className="grid grid-cols-3 gap-4 text-center">
                                    {[...Array(3)].map((_, i) => (
                                      <div key={i} className="space-y-1">
                                        <div className="h-5 bg-gray-200 rounded w-8 animate-pulse"></div>
                                        <div className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : organizationsError ? (
                      // Error State
                      <Card className="credential-capital-card border-red-200 bg-red-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center space-x-3">
                            <AlertCircle className="h-5 w-5 text-red-600" />
                            <div>
                              <p className="text-red-800 font-medium">Failed to load organizations</p>
                              <p className="text-red-600 text-sm">{organizationsError}</p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={refreshData}
                              className="ml-auto"
                            >
                              Retry
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ) : filteredTenants.length === 0 ? (
                      // Empty state
                      <Card className="credential-capital-card">
                        <CardContent className="pt-12 pb-12 text-center">
                          <div className="bg-cool-gray-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-electric-steel" />
                          </div>
                          <h3 className="text-lg font-semibold text-charcoal-ink mb-2">
                            {searchTerm ? 'No organizations found' : 'No organizations yet'}
                          </h3>
                          <p className="text-warm-slate mb-4">
                            {searchTerm 
                              ? `No organizations match "${searchTerm}". Try a different search term.`
                              : 'Get started by creating your first organization.'
                            }
                          </p>
                          {!searchTerm && (
                            <Button 
                              onClick={() => setShowCreateTenant(true)}
                              className="credential-capital-button-primary"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Create First Organization
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      // Real data display
                      filteredTenants.map((tenant) => (
                        <Card key={tenant.id} className="credential-capital-card hover:shadow-lg transition-shadow">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="bg-cool-gray-100 p-3 rounded-xl">
                                  <Building2 className="h-6 w-6 text-electric-steel" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-lg text-charcoal-ink">
                                    {tenant.name}
                                  </h3>
                                  <p className="text-sm text-warm-slate">
                                    {tenant.subdomain}.credentialcapital.com
                                  </p>
                                  <p className="text-xs text-warm-slate mt-1">
                                    Created: {tenant.createdAt}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge 
                                    className={tenant.status === 'active' 
                                      ? 'verified-badge' 
                                      : 'bg-red-100 text-red-700'
                                    }
                                  >
                                    {tenant.status === 'active' ? (
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                    ) : null}
                                    {tenant.status}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {tenant.growth}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center space-x-6">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div>
                                    <p className="font-semibold text-charcoal-ink">{tenant.users}</p>
                                    <p className="text-xs text-warm-slate">Users</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-charcoal-ink">{tenant.credentials}</p>
                                    <p className="text-xs text-warm-slate">Credentials</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-charcoal-ink">{tenant.verifications}</p>
                                    <p className="text-xs text-warm-slate">Verifications</p>
                                  </div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="credential-capital-button-secondary"
                                >
                                  <Settings className="h-4 w-4 mr-2" />
                                  Manage
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* New Base Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <Card className="credential-capital-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-xl font-display text-charcoal-ink">
                    Base Template Management
                  </CardTitle>
                  <CardDescription className="text-warm-slate">
                    Manage and configure base templates for the platform
                  </CardDescription>
                </div>
                <ComingSoonPill />
              </CardHeader>
              <CardContent>
                <BaseTemplateManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="connections" className="space-y-6">
            <Card className="credential-capital-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-xl font-display text-charcoal-ink">
                    Platform Network Connections
                  </CardTitle>
                  <CardDescription className="text-warm-slate">
                    Overview of organizational connections across the entire platform
                  </CardDescription>
                </div>
                <ComingSoonPill />
              </CardHeader>
              <CardContent>
                <OrganizationConnections />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {/* Error Display */}
            {analyticsError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">
                  {analyticsError}
                </AlertDescription>
              </Alert>
            )}

            {/* Loading State */}
            {analyticsLoading && !platformData && (
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-steel mx-auto mb-4"></div>
                  <p className="text-warm-slate">Loading platform analytics...</p>
                </div>
              </div>
            )}

            {/* Real Analytics Data */}
            {platformData && (
              <>
                {/* Platform Overview */}
                <Card className="credential-capital-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-display text-charcoal-ink">
                      Platform Overview
                    </CardTitle>
                    <CardDescription className="text-warm-slate">
                      Real-time system metrics and statistics
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Building2 className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Organizations</span>
                        </div>
                        <p className="text-2xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_organizations.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Users className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Total Users</span>
                        </div>
                        <p className="text-2xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_users.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Shield className="h-5 w-5 text-verified-green" />
                          <span className="text-sm text-warm-slate">Credentials</span>
                        </div>
                        <p className="text-2xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_credentials.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Layers className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Templates</span>
                        </div>
                        <p className="text-2xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_templates.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Organization Breakdown */}
                <Card className="credential-capital-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-display text-charcoal-ink">
                      Organization Breakdown
                    </CardTitle>
                    <CardDescription className="text-warm-slate">
                      Detailed metrics per organization
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {platformData.organizations.slice(0, 10).map((org, index) => (
                        <div key={org.org_id} className="flex items-center justify-between p-3 bg-cool-gray-100 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-charcoal-ink">
                              {index + 1}.
                            </span>
                            <div>
                              <p className="font-medium text-charcoal-ink">{org.org_name}</p>
                              <p className="text-xs text-warm-slate">ID: {org.org_id.slice(0, 8)}...</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-warm-slate">
                              <Users className="h-4 w-4 inline mr-1" />
                              {org.user_count}
                            </span>
                            <span className="text-electric-steel">
                              <Shield className="h-4 w-4 inline mr-1" />
                              {org.credential_count}
                            </span>
                            <span className="text-verified-green">
                              <Layers className="h-4 w-4 inline mr-1" />
                              {org.template_count}
                            </span>
                          </div>
                        </div>
                      ))}
                      {platformData.organizations.length > 10 && (
                        <div className="text-center text-sm text-warm-slate">
                          ... and {platformData.organizations.length - 10} more organizations
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Refresh Button */}
                <div className="flex justify-center">
                  <Button 
                    onClick={refreshData}
                    disabled={analyticsLoading}
                    className="credential-capital-button"
                  >
                    {analyticsLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh Analytics
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card className="credential-capital-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-xl font-display text-charcoal-ink">
                    System Configuration
                  </CardTitle>
                  <CardDescription className="text-warm-slate">
                    Platform-wide settings and configurations
                  </CardDescription>
                </div>
                <ComingSoonPill />
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-charcoal-ink">API Settings</h3>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-charcoal-ink">Rate Limit (requests/minute)</Label>
                          <Input defaultValue="1000" className="credential-capital-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-charcoal-ink">Max File Size (MB)</Label>
                          <Input defaultValue="10" className="credential-capital-input" />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="font-semibold text-charcoal-ink">Security Settings</h3>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-charcoal-ink">Session Timeout (minutes)</Label>
                          <Input defaultValue="60" className="credential-capital-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-charcoal-ink">Password Min Length</Label>
                          <Input defaultValue="8" className="credential-capital-input" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-semibold text-charcoal-ink">Template Management Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-charcoal-ink">Default Template Inheritance</Label>
                        <Input defaultValue="Enabled" className="credential-capital-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-charcoal-ink">Template Approval Required</Label>
                        <Input defaultValue="Yes" className="credential-capital-input" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <Button className="credential-capital-button-primary">
                      Save Settings
                    </Button>
                    <Button variant="outline" className="credential-capital-button-secondary">
                      Reset to Defaults
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}